import { inject, Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private authService: AuthService,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Get the token from localStorage
    const token = localStorage.getItem('access_token');
    
    // Only add the token for API requests to your backend
    if (token) {
      const authRequest = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
      
      return next.handle(authRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          // Handle 401 Unauthorized errors
          if (error.status === 401) {
            console.log('401 Unauthorized - attempting to refresh token');
            
            // Try to refresh the token and retry the request
            return this.authService.handleAuthError(error, () => {
              // Get the new token from localStorage
              const newToken = localStorage.getItem('access_token');
              
              // Create a new request with the new token
              const retryRequest = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${newToken}`
                }
              });
              
              // Retry the request with the new token
              return next.handle(retryRequest);
            });
          }
          
          return throwError(() => error);
        })
      );
    }
    
    // For other requests, don't add the token
    return next.handle(request);
  }
}

// Update the functional interceptor - inject dependencies at the top level
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>, 
  next: HttpHandlerFn
) => {
  // Inject dependencies properly at the top level
  const authService = inject(AuthService);
  const router = inject(Router);
  
  // Get the token from localStorage
  const token = localStorage.getItem('access_token');
  
  if (req.url.includes('dispatch-service-dev.fly.dev') && token) {
    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Handle 401 Unauthorized errors
        if (error.status === 401) {
          console.log('401 Unauthorized - attempting to refresh token');
          
          // Try to refresh the token and retry the request
          return authService.refreshToken().pipe(
            switchMap(newToken => {
              if (newToken) {
                // Create a new request with the new token
                const retryRequest = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken}`
                  }
                });
                
                // Retry the request with the new token
                return next(retryRequest);
              }
              
              // If token refresh failed, throw the original error
              return throwError(() => error);
            }),
            catchError(refreshError => {
              console.error('Error refreshing token:', refreshError);
              
              // If refresh fails, redirect to login
              router.navigate(['/login']);
              
              return throwError(() => error);
            })
          );
        }
        
        return throwError(() => error);
      })
    );
  }
  
  return next(req);
}; 